import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { authAPI, twoFAAPI } from '../services/api';

const TwoFactorVerification = ({ email, userId, onSuccess, onCancel }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (code.length !== 6) {
      toast.error('Please enter a 6-digit verification code');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.login2FA(userId, code);
      
      // Store token and user data
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success('Login successful!');
      onSuccess(response.data);
    } catch (error) {
      toast.error(error.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    try {
      await twoFAAPI.sendLoginCode(email);
      toast.success('New verification code sent to your email');
      setTimeLeft(600); // Reset timer
    } catch (error) {
      toast.error('Failed to resend code: ' + error.message);
    } finally {
      setResending(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
  };

  return (
    <div className="login-container-split">
      {/* Left Side - 2FA Form */}
      <div className="twofa-form-side">
        <div className="twofa-form-container">
          <div className="twofa-form-header">
            <div className="twofa-form-icon">
              <img 
                src="/assets/logo.png" 
                alt="OUK Logo" 
                className="login-logo"
              />
            </div>
            <h2 className="twofa-form-title">Two-Factor Authentication</h2>
            <p className="twofa-form-subtitle">We've sent a verification code to</p>
            <div className="email-display">{email}</div>
          </div>
          
          <form onSubmit={handleSubmit} className="twofa-form">
            <div className="form-group">
              <label className="form-label">Verification Code</label>
              <input
                type="text"
                className="login-input code-input"
                placeholder="000000"
                value={code}
                onChange={handleCodeChange}
                maxLength="6"
                autoComplete="one-time-code"
                required
              />
              <p className="input-help">Enter the 6-digit code from your email</p>
            </div>

            {/* Timer */}
            <div className="timer-section">
              {timeLeft > 0 ? (
                <div className="timer-active">
                  <span className="timer-icon">⏱️</span>
                  <span>Code expires in <strong>{formatTime(timeLeft)}</strong></span>
                </div>
              ) : (
                <div className="timer-expired">
                  <span className="timer-icon">⚠️</span>
                  <span><strong>Code has expired</strong></span>
                </div>
              )}
            </div>

            <button 
              type="submit" 
              className="login-submit-btn" 
              disabled={loading || code.length !== 6}
              style={{
                opacity: (loading || code.length !== 6) ? 0.6 : 1
              }}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <span>🔄</span>
                  <span>Verifying...</span>
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <span>🔓</span>
                  <span>Verify & Login</span>
                </span>
              )}
            </button>
            
            <button
              type="button"
              className="login-cancel-btn"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>

            {/* Resend */}
            <div className="resend-section">
              <button
                type="button"
                className="btn-link"
                onClick={handleResendCode}
                disabled={resending || timeLeft > 540} // Allow resend after 1 minute
              >
                {resending ? 'Sending...' : 'Resend Code'}
              </button>
              {timeLeft > 540 && (
                <p className="resend-timer">
                  You can request a new code in {formatTime(timeLeft - 540)}
                </p>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Right Side - Security Gradient with Messaging */}
      <div className="twofa-gradient-side">
        <div className="twofa-gradient-content">
          <div className="security-hero">
            <div className="security-icon">🛡️</div>
            <h1 className="security-title">Your Security Matters</h1>
            <p className="security-subtitle">Two-factor authentication adds an extra layer of protection</p>
          </div>
          
          <div className="security-features">
            <div className="security-feature">
              <div className="feature-icon">🔒</div>
              <div className="feature-content">
                <h3>Enhanced Protection</h3>
                <p>Even if someone knows your password, they can't access your account without your email verification</p>
              </div>
            </div>
            
            <div className="security-feature">
              <div className="feature-icon">📧</div>
              <div className="feature-content">
                <h3>Email Verification</h3>
                <p>We send a unique code to your registered email address that expires in 10 minutes</p>
              </div>
            </div>
            
            <div className="security-feature">
              <div className="feature-icon">⚡</div>
              <div className="feature-content">
                <h3>Quick & Secure</h3>
                <p>The verification process takes just seconds but provides enterprise-level security</p>
              </div>
            </div>
          </div>
          
          <div className="security-footer">
            <p>🔐 <strong>OUK Student Recruiters</strong> - Protecting your data with industry-standard security practices</p>
          </div>
        </div>
        
        {/* Animated Security Elements */}
        <div className="security-shapes">
          <div className="security-shape shape-1"></div>
          <div className="security-shape shape-2"></div>
          <div className="security-shape shape-3"></div>
          <div className="security-shape shape-4"></div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorVerification;